import { useEffect } from "react";
import { useLocation } from "react-router-dom";

// Easing function for smooth animation
const easeInOutCubic = (t: number): number => {
  return t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
};

const smoothScrollTo = (targetPosition: number, duration: number = 300) => {
  const startPosition = window.pageYOffset;
  const distance = targetPosition - startPosition;
  let startTime: number | null = null;

  const animation = (currentTime: number) => {
    if (startTime === null) startTime = currentTime;
    const timeElapsed = currentTime - startTime;
    const progress = Math.min(timeElapsed / duration, 1);
    const easedProgress = easeInOutCubic(progress);
    
    window.scrollTo(0, startPosition + distance * easedProgress);
    
    if (timeElapsed < duration) {
      requestAnimationFrame(animation);
    }
  };

  requestAnimationFrame(animation);
};

const scrollToElement = (hash: string) => {
  const element = document.querySelector(hash);
  if (element) {
    // Account for fixed header (80px) + sticky CTA button (~48px)
    const headerOffset = 140;
    const elementPosition = element.getBoundingClientRect().top;
    const offsetPosition = elementPosition + window.pageYOffset - headerOffset;
    smoothScrollTo(offsetPosition, 300);
  }
};

export const useScrollToSection = () => {
  const location = useLocation();

  // Handle hash links for same-page navigation
  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest('a');
      
      if (anchor) {
        const href = anchor.getAttribute('href');
        // Only intercept pure hash links (#section) on the current page
        // Don't intercept links like /#section which need full navigation
        if (href?.startsWith('#') && !href.startsWith('/#') && window.location.pathname === '/') {
          e.preventDefault();
          scrollToElement(href);
          window.history.pushState(null, '', href);
        }
      }
    };

    document.addEventListener('click', handleClick);
    return () => document.removeEventListener('click', handleClick);
  }, []);

  // Handle initial load and route changes with hash
  useEffect(() => {
    if (location.hash) {
      setTimeout(() => {
        scrollToElement(location.hash);
      }, 100);
    } else if (location.pathname === "/") {
      smoothScrollTo(0, 300);
    }
  }, [location.hash, location.pathname]);
};
